import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { linksApi, affiliatesApi } from '@/lib/api';
import { AffiliateLink } from '@/types';
import {
  PlusIcon,
  ClipboardIcon,
  TrashIcon,
  PencilIcon,
  RocketLaunchIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

interface AffiliateProfile {
  affiliate: {
    affiliateId: string;
    referralCode: string;
    status: string;
  };
  organization: {
    organizationId: string;
    name: string;
    slug: string;
    website?: string;
  } | null;
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export default function AffiliateLinks() {
  const [links, setLinks] = useState<AffiliateLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLink, setEditingLink] = useState<AffiliateLink | null>(null);
  const [newLink, setNewLink] = useState({ destinationUrl: '', name: '' });
  const [editLink, setEditLink] = useState({ destinationUrl: '', name: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [profile, setProfile] = useState<AffiliateProfile | null>(null);

  const fetchData = async () => {
    try {
      // Fetch profile first to check if user is approved
      const profileResponse = await affiliatesApi.getMe().catch(() => null);

      if (profileResponse?.data?.success) {
        const profileData = profileResponse.data.data;
        setProfile(profileData);

        // If user has an approved profile, they can manage links
        if (profileData?.affiliate?.status === 'approved') {
          try {
            const linksResponse = await linksApi.list();
            if (linksResponse.data.success) {
              setLinks(linksResponse.data.data.links);
            }
          } catch (linkError) {
            // Links fetch failed but user is approved, show empty state not onboarding
            console.error('Failed to fetch links:', linkError);
            setLinks([]);
          }
        } else {
          // User has a profile but isn't approved yet
          setNeedsOnboarding(true);
        }
      } else {
        // No profile found - user needs to join a program
        setNeedsOnboarding(true);
      }
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { error?: { code?: string; message?: string } } } };
      // Check if user needs to join a program first
      if (err.response?.status === 403 ||
          (err.response?.status === 404 && err.response?.data?.error?.code === 'NO_AFFILIATE_PROFILE')) {
        setNeedsOnboarding(true);
      } else {
        console.error('Failed to fetch links:', error);
        toast.error('Failed to load links');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLinks = async () => {
    try {
      const response = await linksApi.list();
      if (response.data.success) {
        setLinks(response.data.data.links);
      }
    } catch (error: unknown) {
      console.error('Failed to fetch links:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const response = await linksApi.create({
        destinationUrl: newLink.destinationUrl,
        name: newLink.name || undefined,
      });

      if (response.data.success) {
        toast.success('Link created successfully!');
        setShowCreateModal(false);
        setNewLink({ destinationUrl: '', name: '' });
        fetchLinks();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to create link');
    } finally {
      setIsCreating(false);
    }
  };

  // Tracking URLs go through the API, not the frontend
  const apiUrl = import.meta.env.VITE_API_URL || '';

  const handleCopy = (link: AffiliateLink) => {
    const trackingUrl = `${apiUrl}/t/${link.shortCode}`;
    navigator.clipboard.writeText(trackingUrl);
    toast.success('Link copied to clipboard!');
  };

  const handleDelete = async (linkId: string) => {
    if (!confirm('Are you sure you want to delete this link?')) return;

    try {
      await linksApi.delete(linkId);
      toast.success('Link deleted');
      fetchLinks();
    } catch {
      toast.error('Failed to delete link');
    }
  };

  const handleEditClick = (link: AffiliateLink) => {
    setEditingLink(link);
    setEditLink({
      destinationUrl: link.destinationUrl,
      name: link.name || '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLink) return;

    setIsUpdating(true);
    try {
      const response = await linksApi.update(editingLink.linkId, {
        destinationUrl: editLink.destinationUrl,
        name: editLink.name || undefined,
      });

      if (response.data.success) {
        toast.success('Link updated successfully!');
        setShowEditModal(false);
        setEditingLink(null);
        fetchLinks();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to update link');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  // Show onboarding message for affiliates who haven't joined a program
  if (needsOnboarding) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-rust/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <RocketLaunchIcon className="w-10 h-10 text-rust" />
          </div>
          <h2 className="text-2xl font-display font-bold text-charcoal mb-3">Join an Affiliate Program First</h2>
          <p className="text-charcoal/60 max-w-md mx-auto mb-8">
            Before you can create tracking links, you need to join an affiliate program.
            Browse available programs and apply to ones that match your audience.
          </p>
          <Link
            to="/catalog"
            className="btn btn-primary px-8 py-3 text-lg inline-flex items-center gap-2"
          >
            <MagnifyingGlassIcon className="w-5 h-5" />
            Browse Affiliate Programs
          </Link>
        </div>

        {/* Getting Started Steps */}
        <div className="rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/30">
            <h3 className="text-lg font-display font-semibold text-charcoal">How It Works</h3>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-rust rounded-full flex items-center justify-center text-white font-bold">
                  1
                </div>
                <div>
                  <h4 className="text-charcoal font-medium">Find a Program</h4>
                  <p className="text-charcoal/60 text-sm mt-1">
                    Browse our catalog of affiliate programs or use an invite link from an organization.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-tan rounded-full flex items-center justify-center text-charcoal font-bold">
                  2
                </div>
                <div>
                  <h4 className="text-charcoal font-medium">Get Approved</h4>
                  <p className="text-charcoal/60 text-sm mt-1">
                    Some programs auto-approve affiliates, others review applications manually.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-sage rounded-full flex items-center justify-center text-white font-bold">
                  3
                </div>
                <div>
                  <h4 className="text-charcoal font-medium">Create Links & Earn</h4>
                  <p className="text-charcoal/60 text-sm mt-1">
                    Once approved, come back here to create tracking links and start earning commissions!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-charcoal">
            {profile?.organization ? `${profile.organization.name} Links` : 'Affiliate Links'}
          </h2>
          <p className="text-charcoal/60 mt-1">Create and manage your tracking links</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Create Link
        </button>
      </div>

      {/* Links Table */}
      <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
        {links.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-tan/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <PlusIcon className="w-8 h-8 text-charcoal/40" />
            </div>
            <h3 className="text-lg font-medium text-charcoal mb-2">No links yet</h3>
            <p className="text-charcoal/60 mb-6">Create your first affiliate link to start tracking.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              Create Link
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50 border-b border-tan/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Link</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Tracking URL</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Clicks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Conversions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tan/20">
                {links.map((link) => (
                  <tr key={link.linkId} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-charcoal">
                          {link.name || 'Unnamed Link'}
                        </p>
                        <p className="text-xs text-charcoal/50 truncate max-w-xs">
                          → {link.destinationUrl}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-xs text-rust bg-rust/10 px-2 py-1 rounded break-all">
                        {apiUrl}/t/{link.shortCode}
                      </code>
                    </td>
                    <td className="px-6 py-4 font-serif text-charcoal">{formatNumber(link.totalClicks ?? 0)}</td>
                    <td className="px-6 py-4 font-serif text-charcoal">{formatNumber(link.totalConversions ?? 0)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCopy(link)}
                          className="p-2 text-charcoal/50 hover:text-rust hover:bg-rust/10 rounded-lg transition-colors"
                          title="Copy tracking link"
                        >
                          <ClipboardIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditClick(link)}
                          className="p-2 text-charcoal/50 hover:text-charcoal hover:bg-tan/30 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(link.linkId)}
                          className="p-2 text-charcoal/50 hover:text-rust hover:bg-rust/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60">
          <div className="rounded-xl bg-warm-white shadow-lg w-full max-w-md animate-fade-in">
            <div className="px-6 py-4 border-b border-tan/30 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-display font-semibold text-charcoal">Create Link</h3>
                {profile?.organization && (
                  <p className="text-xs text-charcoal/60 mt-0.5">
                    for {profile.organization.name}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-charcoal/50 hover:text-charcoal text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Destination URL *</label>
                <input
                  type="url"
                  value={newLink.destinationUrl}
                  onChange={(e) => setNewLink({ ...newLink, destinationUrl: e.target.value })}
                  className="input"
                  placeholder={profile?.organization?.website || 'https://example.com/product'}
                  required
                />
                <p className="text-xs text-charcoal/50 mt-1">
                  Where should visitors be sent when they click your link?
                </p>
                {profile?.organization?.website && !newLink.destinationUrl && (
                  <button
                    type="button"
                    onClick={() => setNewLink({ ...newLink, destinationUrl: profile.organization?.website || '' })}
                    className="text-xs text-rust hover:text-rust-light mt-1"
                  >
                    Use {profile.organization.name}&apos;s website
                  </button>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Name (optional)</label>
                <input
                  type="text"
                  value={newLink.name}
                  onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                  className="input"
                  placeholder={profile?.organization ? `${profile.organization.name} Link` : 'My Product Link'}
                />
              </div>
              <div className="bg-cream/50 rounded-lg p-3 text-xs text-charcoal/70">
                <strong>How it works:</strong> We&apos;ll create a tracking link that redirects to your destination URL. When visitors click and make a purchase, you earn commissions!
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="btn btn-primary flex-1"
                >
                  {isCreating ? 'Creating...' : 'Create Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60">
          <div className="rounded-xl bg-warm-white shadow-lg w-full max-w-md animate-fade-in">
            <div className="px-6 py-4 border-b border-tan/30 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-display font-semibold text-charcoal">Edit Link</h3>
                <p className="text-xs text-charcoal/60 mt-0.5">
                  {editingLink.shortCode}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingLink(null);
                }}
                className="text-charcoal/50 hover:text-charcoal text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Destination URL *</label>
                <input
                  type="url"
                  value={editLink.destinationUrl}
                  onChange={(e) => setEditLink({ ...editLink, destinationUrl: e.target.value })}
                  className="input"
                  placeholder="https://example.com/product"
                  required
                />
                <p className="text-xs text-charcoal/50 mt-1">
                  Where should visitors be sent when they click your link?
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1">Name (optional)</label>
                <input
                  type="text"
                  value={editLink.name}
                  onChange={(e) => setEditLink({ ...editLink, name: e.target.value })}
                  className="input"
                  placeholder="My Product Link"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingLink(null);
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  className="btn btn-primary flex-1"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
